import { authStore } from '../auth/authStore';
import {
  authHeader,
  getUserNoFromToken,
  isAppEnvironment,
  refreshAccessToken,
} from './auth';

const API_BASE = (process.env.REACT_APP_API_URL ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '') : '');
const TRACE_STORAGE_KEY = 'agent-ui-consult-traces';
const TRACE_STORAGE_LIMIT = 12;
const REVIEW_STATUS_LABELS = {
  pending_triage: '분류 대기',
  assigned: '담당자 배정',
  in_review: '검토 중',
  resolved: '해결됨',
  dismissed: '검토 제외',
  reopened: '재검토',
};
const API_ERROR_FIELD_LABELS = {
  'query.user_no': '사용자 번호',
  'query.patient_id': '환자 ID',
  'query.status': '상태',
  'body.status': '상태',
  'body.reason': '사유',
  'body.assigned_to': '담당자',
  'body.note_text': '노트',
  'body.action_type': '조치 유형',
  'body.decision': '초안 결정',
  'body.draft_type': '초안 유형',
  'body.source_agent': '생성 에이전트',
  'body.provenance_json': '출처 정보',
};

function getPrimaryTraceStorage() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage;
}

function getLegacyTraceStorage() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.sessionStorage;
}

export function buildApiUrl(path) {
  if (!path.startsWith('/')) {
    return `${API_BASE}/${path}`;
  }
  return `${API_BASE}${path}`;
}

async function parseResponse(response) {
  const raw = await response.text();
  try {
    return raw ? JSON.parse(raw) : {};
  } catch {
    return { raw };
  }
}

function formatApiErrorDetail(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    return translateApiErrorMessage(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => formatApiErrorDetail(item))
      .filter(Boolean)
      .join(' / ');
  }
  if (typeof value === 'object') {
    const location = Array.isArray(value.loc) ? value.loc.join('.') : value.loc;
    const message = value.msg || value.message || value.detail || value.error;
    if (location && message) {
      return `${formatApiErrorLocation(location)}: ${formatApiErrorDetail(message)}`;
    }
    if (message) {
      return formatApiErrorDetail(message);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function formatApiErrorLocation(location) {
  const normalized = String(location || '').trim();
  return API_ERROR_FIELD_LABELS[normalized] || normalized;
}

function translateApiErrorMessage(message) {
  const normalized = String(message || '').trim();
  const lower = normalized.toLowerCase();
  const translations = {
    'evidence snapshot not found': '근거 스냅샷을 찾을 수 없습니다.',
    'blocked answer snapshot not found': '차단 답변 스냅샷을 찾을 수 없습니다.',
    'snapshot not found': '스냅샷을 찾을 수 없습니다.',
    'authorization header missing or invalid': '인증 정보가 없거나 올바르지 않습니다.',
    review_role_required: '검토 권한이 필요합니다.',
    admin_only: '관리자 권한이 필요합니다.',
    read_service_not_allowed: '검토 케이스 조회 권한이 없는 서비스입니다.',
    write_service_not_allowed: '검토 케이스 변경 권한이 없는 서비스입니다.',
    actor_context_missing: '변경 작업을 수행할 사용자 정보가 없습니다.',
    actor_id_mismatch: '요청 사용자와 작업자 정보가 일치하지 않습니다.',
    author_id_mismatch: '요청 사용자와 노트 작성자 정보가 일치하지 않습니다.',
    executed_by_mismatch: '요청 사용자와 조치 실행자 정보가 일치하지 않습니다.',
    assistant_draft_decision_invalid: '초안 결정은 채택 또는 반려만 선택할 수 있습니다.',
    review_case_not_found: '검토 케이스를 찾을 수 없습니다.',
    clinical_review_service_unavailable: '검토 서비스에 연결할 수 없습니다.',
    clinical_review_service_upstream_error: '검토 서비스에서 오류가 발생했습니다.',
    clinical_review_service_invalid_json: '검토 서비스 응답 형식이 올바르지 않습니다.',
    clinical_review_service_request_rejected: '검토 서비스가 요청을 거부했습니다.',
    review_assistant_agent_unavailable: '검토 보조 서비스에 연결할 수 없습니다.',
    review_assistant_agent_upstream_error: '검토 보조 서비스에서 오류가 발생했습니다.',
    review_assistant_agent_invalid_json: '검토 보조 서비스 응답 형식이 올바르지 않습니다.',
    review_assistant_agent_request_rejected: '검토 보조 서비스가 요청을 거부했습니다.',
    'caseid is required': '검토 케이스 ID가 필요합니다.',
    'snapshottype is required': '스냅샷 유형이 필요합니다.',
    'userno, patientid, and decid are required': '사용자 번호, 환자 ID, DEC ID가 필요합니다.',
    'input should be a valid integer, unable to parse string as an integer': '정수를 입력해야 합니다.',
    'field required': '필수 값입니다.',
    'string should have at least 1 character': '값을 입력해야 합니다.',
  };

  const transition = lower.match(/^([a-z_]+)\s*->\s*([a-z_]+)\s+is not allowed$/);
  if (transition) {
    const fromStatus = REVIEW_STATUS_LABELS[transition[1]] || transition[1];
    const toStatus = REVIEW_STATUS_LABELS[transition[2]] || transition[2];
    return `${fromStatus} 상태에서 ${toStatus} 상태로 바로 변경할 수 없습니다. 허용된 중간 상태로 먼저 변경하세요.`;
  }

  return translations[lower] || translations[normalized] || normalized;
}

function normalizeIntegerQueryValue(value) {
  const trimmed = String(value ?? '').trim();
  return /^\d+$/.test(trimmed) ? trimmed : '';
}

async function doFetch(path, options = {}) {
  const response = await fetch(buildApiUrl(path), {
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
      ...(options.headers || {}),
    },
    ...options,
  });

  const parsed = await parseResponse(response);
  return { response, parsed };
}

function redirectToLogin() {
  const loginPath = isAppEnvironment() ? '/app/login' : '/';
  if (window.location.pathname !== loginPath) {
    window.location.replace(loginPath);
  }
}

export async function fetchJson(path, options = {}) {
  let { response, parsed } = await doFetch(path, options);

  if (response.status === 401 && !String(path).includes('/api/v1/auth/refresh')) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      ({ response, parsed } = await doFetch(path, options));
    } else if (!authStore.getSnapshot().accessToken) {
      authStore.logout({ reason: 'agent-ui-api-unauthorized' });
      redirectToLogin();
    } else {
      throw new Error('인증 서버 확인을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    }
  }

  if (!response.ok) {
    const detail = formatApiErrorDetail(parsed?.detail || parsed?.message || parsed?.raw) || `HTTP ${response.status}`;
    throw new Error(detail);
  }

  return parsed;
}

export function createSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function createTraceId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function buildGatewayConsultPayload({ patientId, reportId, question, sessionId, history }) {
  return {
    patient_id: Number(patientId),
    report_id: reportId ? Number(reportId) : undefined,
    question: question.trim(),
    session_id: sessionId,
    history: Array.isArray(history)
      ? history
          .filter((item) => item && item.role && item.content)
          .map((item) => ({
            role: item.role,
            content: item.content,
          }))
      : [],
  };
}

export async function fetchConsultationPatients(userNo) {
  if (!userNo) {
    return [];
  }

  const data = await fetchJson(`/api/v1/consultation/${userNo}/patients?t=${Date.now()}`);
  return Array.isArray(data?.patients) ? data.patients : [];
}

export async function fetchLatestUserReport(userNo, patientId) {
  if (!userNo) {
    return null;
  }

  const params = new URLSearchParams();
  params.set('fields', 'report_id,patient_id,created_at,title,is_latest,summary');
  if (patientId) {
    params.set('patient_id', String(patientId));
  }
  params.set('include_superseded', 'false');
  params.set('limit', '1');
  params.set('offset', '0');

  const data = await fetchJson(`/api/v1/users/${userNo}/reports?${params.toString()}`);
  const list = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
      ? data
      : [];

  return list[0] ?? null;
}

export function readConsultTraces() {
  const storages = [getPrimaryTraceStorage(), getLegacyTraceStorage()].filter(Boolean);
  try {
    for (const storage of storages) {
      const raw = storage.getItem(TRACE_STORAGE_KEY);
      if (!raw) {
        continue;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
    return [];
  } catch {
    return [];
  }
}

export function writeConsultTrace(trace) {
  const next = [trace, ...readConsultTraces()].slice(0, TRACE_STORAGE_LIMIT);
  try {
    const payload = JSON.stringify(next);
    getPrimaryTraceStorage()?.setItem(TRACE_STORAGE_KEY, payload);
    getLegacyTraceStorage()?.setItem(TRACE_STORAGE_KEY, payload);
  } catch {
    // ignore
  }
}

export function clearConsultTraces() {
  try {
    getPrimaryTraceStorage()?.removeItem(TRACE_STORAGE_KEY);
    getLegacyTraceStorage()?.removeItem(TRACE_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getResolvedUserNo() {
  return authStore.getSnapshot().userNo || getUserNoFromToken() || window.localStorage.getItem('user_no') || '';
}

export function summarizePersonalContext(personalContext) {
  if (!personalContext || typeof personalContext !== 'object') {
    return [];
  }
  if (Array.isArray(personalContext.summary_lines)) {
    return personalContext.summary_lines.filter(Boolean);
  }
  return [];
}

export async function fetchReviewCases({
  status,
  userNo,
  patientId,
  limit = 40,
  offset = 0,
} = {}) {
  const params = new URLSearchParams();

  if (status) {
    params.set('status', status);
  }
  const normalizedUserNo = normalizeIntegerQueryValue(userNo);
  const normalizedPatientId = normalizeIntegerQueryValue(patientId);

  if (normalizedUserNo) {
    params.set('user_no', normalizedUserNo);
  }
  if (normalizedPatientId) {
    params.set('patient_id', normalizedPatientId);
  }
  params.set('limit', String(limit));
  params.set('offset', String(offset));

  return fetchJson(`/api/v1/review-cases?${params.toString()}`);
}

export async function fetchReviewCaseDetail(caseId) {
  if (!caseId) {
    throw new Error('검토 케이스 ID가 필요합니다.');
  }
  return fetchJson(`/api/v1/review-cases/${encodeURIComponent(caseId)}`);
}

export async function fetchReviewAssistantSummary(caseId) {
  if (!caseId) {
    throw new Error('검토 케이스 ID가 필요합니다.');
  }
  return fetchJson(`/api/v1/review-assistant/review-cases/${encodeURIComponent(caseId)}/summary`);
}

export async function fetchReviewAssistantDrafts(caseId) {
  if (!caseId) {
    throw new Error('검토 케이스 ID가 필요합니다.');
  }
  return fetchJson(`/api/v1/review-assistant/review-cases/${encodeURIComponent(caseId)}/drafts`);
}

export async function assignReviewCase(caseId, payload) {
  if (!caseId) {
    throw new Error('검토 케이스 ID가 필요합니다.');
  }
  return fetchJson(`/api/v1/review-cases/${encodeURIComponent(caseId)}/assign`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateReviewCaseStatus(caseId, payload) {
  if (!caseId) {
    throw new Error('검토 케이스 ID가 필요합니다.');
  }
  return fetchJson(`/api/v1/review-cases/${encodeURIComponent(caseId)}/status`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function createReviewCaseNote(caseId, payload) {
  if (!caseId) {
    throw new Error('검토 케이스 ID가 필요합니다.');
  }
  return fetchJson(`/api/v1/review-cases/${encodeURIComponent(caseId)}/notes`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function createReviewCaseAction(caseId, payload) {
  if (!caseId) {
    throw new Error('검토 케이스 ID가 필요합니다.');
  }
  return fetchJson(`/api/v1/review-cases/${encodeURIComponent(caseId)}/actions`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function recordReviewAssistantDraftDecision(caseId, payload) {
  if (!caseId) {
    throw new Error('검토 케이스 ID가 필요합니다.');
  }
  return fetchJson(`/api/v1/review-cases/${encodeURIComponent(caseId)}/assistant-draft-decisions`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchReviewSnapshot({
  snapshotType,
  userNo,
  patientId,
  decId,
}) {
  if (!snapshotType) {
    throw new Error('스냅샷 유형이 필요합니다.');
  }
  if (!userNo || !patientId || !decId) {
    throw new Error('사용자 번호, 환자 ID, DEC ID가 필요합니다.');
  }

  const params = new URLSearchParams();
  params.set('user_no', String(userNo));
  params.set('patient_id', String(patientId));
  params.set('dec_id', String(decId));

  return fetchJson(`/api/v1/review-snapshots/${encodeURIComponent(snapshotType)}?${params.toString()}`);
}
